import { useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { communityPosts, donationCampaigns, DonationCampaign } from "@/mocks/community";
import PostCard from "./components/PostCard";
import DonationModal from "./components/DonationModal";
import NewPostModal from "./components/NewPostModal";
import { useSEO, SITE_URL } from "@/hooks/useSEO";

const categoryFilters = ["All", "Medical", "Nutrition", "Mental Health", "Clothing", "Donation", "Baby Care"];

export default function CommunityPage() {
  useSEO({
    title: "Community Care & Infant Help | Donate & Support | Crown Global",
    description: "Join the Crown Global community of doctors, volunteers, and donors helping infants worldwide. Post needs, offer help, and donate to active campaigns supporting babies in need across the globe.",
    keywords: "community care, infant help, donate babies, volunteer, medical support, global community",
    canonical: "/community",
    ogTitle: "Community Care & Infant Help — Crown Global",
    ogDescription: "Doctors, volunteers, mothers, and kind hearts helping infants worldwide. Post needs, offer help, and donate.",
    ogImage: "https://public.readdy.ai/ai/img_res/9be280302c53a817bce18c75b4fde23e.jpg",
    schemaJson: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": `${SITE_URL}/community`,
          "url": `${SITE_URL}/community`,
          "name": "Community Care & Infant Help — Crown Global",
          "description": "A community platform connecting doctors, volunteers, and donors to help infants and families in need worldwide.",
          "isPartOf": { "@id": `${SITE_URL}/#website` },
          "breadcrumb": {
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": SITE_URL },
              { "@type": "ListItem", "position": 2, "name": "Community", "item": `${SITE_URL}/community` }
            ]
          }
        },
        {
          "@type": "DonateAction",
          "@id": `${SITE_URL}/community#donate`,
          "name": "Donate to Infant Care Fund",
          "description": "Donate to help infants worldwide with medical care, nutrition, and clothing.",
          "url": `${SITE_URL}/community`,
          "recipient": {
            "@type": "Organization",
            "name": "Crown Global Community Care Fund",
            "url": `${SITE_URL}/community`
          }
        },
        {
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "Who can post in the community?",
              "acceptedAnswer": { "@type": "Answer", "text": "Anyone can post in the Crown Global community — doctors, volunteers, mothers, donors, and anyone who wants to help or needs help for infants and families." }
            },
            {
              "@type": "Question",
              "name": "How do donations reach infants?",
              "acceptedAnswer": { "@type": "Answer", "text": "Donations go directly to active campaigns for specific infants and families in need. Each campaign shows the goal, amount raised, and the story behind the need." }
            }
          ]
        }
      ]
    }
  });

  const [filter, setFilter] = useState("All");
  const [donationTarget, setDonationTarget] = useState<DonationCampaign | null>(null);
  const [showPost, setShowPost] = useState(false);
  const [postSuccess, setPostSuccess] = useState(false);
  const [generalDonation, setGeneralDonation] = useState(false);

  const filtered = filter === "All" ? communityPosts : communityPosts.filter(p => p.category === filter);

  const totalRaised = donationCampaigns.reduce((a, c) => a + c.raised, 0);
  const totalDonors = donationCampaigns.reduce((a, c) => a + c.donors, 0);

  const generalCampaign: DonationCampaign = {
    id: 99,
    title: "General Infant Care Fund — Help Babies Worldwide",
    description: "Your donation goes directly to providing milk, warm clothes, medical care, and love to infants in need across the world. Every dollar makes a difference.",
    image: "https://readdy.ai/api/search-image?query=group%20of%20babies%20infants%20diverse%20beautiful%20smiling%20warm%20light%20soft%20background%20hope%20love%20care%20touching%20emotional%20photography%20world&width=600&height=400&seq=gen1&orientation=landscape",
    raised: totalRaised,
    goal: 500000,
    donors: totalDonors,
    country: "Worldwide",
    flag: "🌍",
    urgent: false,
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      {showPost && (
        <NewPostModal
          onClose={() => setShowPost(false)}
          onPosted={() => { setShowPost(false); setPostSuccess(true); setTimeout(() => setPostSuccess(false), 4000); }}
        />
      )}
      {(donationTarget || generalDonation) && (
        <DonationModal
          campaign={generalDonation ? generalCampaign : donationTarget}
          onClose={() => { setDonationTarget(null); setGeneralDonation(false); }}
        />
      )}

      <div className="pt-32 pb-16">
        <div className="max-w-screen-2xl mx-auto px-4 md:px-8">

          {/* Hero */}
          <div className="relative rounded-2xl overflow-hidden mb-10">
            <img
              src="https://readdy.ai/api/search-image?query=diverse%20group%20of%20people%20doctors%20volunteers%20mothers%20community%20helping%20each%20other%20warm%20light%20hopeful%20emotional%20photography%20unity%20love%20care%20world&width=1400&height=360&seq=comm1&orientation=landscape"
              alt="Community"
              className="w-full h-60 md:h-80 object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-transparent"></div>
            <div className="absolute inset-0 flex flex-col justify-center px-8 md:px-16">
              <div className="inline-flex items-center gap-2 bg-rose-500/20 border border-rose-400/40 text-rose-300 text-xs font-semibold px-3 py-1.5 rounded-full mb-4 w-fit">
                <i className="ri-heart-fill"></i> People Helping People
              </div>
              <h1 className="text-3xl md:text-5xl font-extrabold text-white mb-3">Community Care & Infant Help</h1>
              <p className="text-white/80 text-base max-w-xl mb-6">Doctors, volunteers, mothers, and kind hearts — all together. Post what you need, offer what you can, and donate to help infants worldwide.</p>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={() => setShowPost(true)}
                  className="bg-rose-500 hover:bg-rose-600 text-white font-bold px-6 py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap text-sm"
                >
                  <i className="ri-edit-line mr-1.5"></i>Post Your Need / Offer
                </button>
                <button
                  onClick={() => setGeneralDonation(true)}
                  className="bg-amber-400 hover:bg-amber-500 text-white font-bold px-6 py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap text-sm"
                >
                  <i className="ri-heart-fill mr-1.5"></i>Donate to Infants 💝
                </button>
              </div>
            </div>
          </div>

          {/* Success toast */}
          {postSuccess && (
            <div className="bg-green-50 border border-green-200 text-green-700 rounded-xl px-5 py-4 mb-6 flex items-center gap-3">
              <i className="ri-checkbox-circle-fill text-xl"></i>
              <span className="text-sm font-semibold">Your post has been shared with the community! Someone will reach out to help. 💙</span>
            </div>
          )}

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
            {[
              { label: "Community Members", value: "48,200+", icon: "ri-group-line", color: "bg-rose-50 text-rose-500" },
              { label: "Doctors & Specialists", value: "1,240", icon: "ri-stethoscope-line", color: "bg-blue-50 text-blue-500" },
              { label: "Total Donated", value: "$" + (totalRaised / 1000).toFixed(0) + "K", icon: "ri-heart-fill", color: "bg-amber-50 text-amber-500" },
              { label: "Infants Helped", value: "12,800+", icon: "ri-emotion-happy-line", color: "bg-green-50 text-green-500" },
            ].map(s => (
              <div key={s.label} className="bg-white rounded-xl p-5 flex items-center gap-4">
                <div className={`w-12 h-12 flex items-center justify-center rounded-xl shrink-0 ${s.color}`}>
                  <i className={`${s.icon} text-2xl`}></i>
                </div>
                <div>
                  <div className="text-xl font-extrabold text-gray-900">{s.value}</div>
                  <div className="text-xs text-gray-400">{s.label}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Community feed */}
            <div className="lg:col-span-2">
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-xl font-extrabold text-gray-900">Community Feed</h2>
                <button
                  onClick={() => setShowPost(true)}
                  className="bg-rose-500 hover:bg-rose-600 text-white text-xs font-bold px-4 py-2 rounded-full transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-add-line mr-1"></i>Post
                </button>
              </div>

              {/* Category filter */}
              <div className="flex flex-wrap gap-2 mb-5">
                {categoryFilters.map(f => (
                  <button
                    key={f}
                    onClick={() => setFilter(f)}
                    className={`text-xs font-semibold px-3 py-1.5 rounded-full transition-colors cursor-pointer whitespace-nowrap ${filter === f ? "bg-rose-500 text-white" : "bg-white border border-gray-200 text-gray-500 hover:border-rose-300 hover:text-rose-400"}`}
                  >
                    {f}
                  </button>
                ))}
              </div>

              <div className="flex flex-col gap-4">
                {filtered.map(post => (
                  <PostCard key={post.id} post={post} />
                ))}
              </div>
            </div>

            {/* Sidebar */}
            <div className="flex flex-col gap-6">
              {/* Big donation CTA */}
              <div className="bg-gradient-to-br from-rose-500 to-rose-600 rounded-2xl p-6 text-white text-center">
                <div className="text-4xl mb-3">👶🍼💝</div>
                <h3 className="text-xl font-extrabold mb-2">Help Infants Worldwide</h3>
                <p className="text-rose-100 text-sm leading-relaxed mb-5">Thousands of babies need milk, clothes, and medical care. Your donation — big or small — saves lives.</p>
                <div className="bg-white/20 rounded-xl p-3 mb-5">
                  <div className="text-2xl font-extrabold">${totalRaised.toLocaleString()}</div>
                  <div className="text-rose-200 text-xs">raised by {totalDonors.toLocaleString()} donors</div>
                </div>
                <button
                  onClick={() => setGeneralDonation(true)}
                  className="w-full bg-white text-rose-500 font-extrabold py-3.5 rounded-full hover:bg-rose-50 transition-colors cursor-pointer whitespace-nowrap text-sm"
                >
                  <i className="ri-heart-fill mr-2"></i>Donate Now 💝
                </button>
              </div>

              {/* Donation campaigns */}
              <div>
                <h3 className="font-extrabold text-gray-900 mb-4">Active Campaigns</h3>
                <div className="flex flex-col gap-4">
                  {donationCampaigns.map(c => {
                    const pct = Math.min(100, Math.round((c.raised / c.goal) * 100));
                    return (
                      <div key={c.id} className="bg-white rounded-xl overflow-hidden">
                        <div className="relative w-full h-32 overflow-hidden">
                          <img src={c.image} alt={c.title} className="w-full h-full object-cover object-top" />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                          {c.urgent && (
                            <span className="absolute top-2 left-2 bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">URGENT</span>
                          )}
                          <div className="absolute bottom-2 left-3 right-3">
                            <p className="text-white font-bold text-xs leading-snug line-clamp-2">{c.title}</p>
                          </div>
                        </div>
                        <div className="p-3">
                          <div className="flex items-center justify-between text-xs mb-1.5">
                            <span className="font-bold text-gray-800">${c.raised.toLocaleString()}</span>
                            <span className="text-gray-400">{pct}%</span>
                          </div>
                          <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden mb-3">
                            <div className="h-full bg-rose-500 rounded-full" style={{ width: `${pct}%` }}></div>
                          </div>
                          <button
                            onClick={() => setDonationTarget(c)}
                            className="w-full bg-rose-500 hover:bg-rose-600 text-white text-xs font-bold py-2 rounded-full transition-colors cursor-pointer whitespace-nowrap"
                          >
                            <i className="ri-heart-fill mr-1"></i>Donate
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Emotion guide */}
              <div className="bg-white rounded-2xl p-5">
                <h3 className="font-bold text-gray-800 mb-3 text-sm">Express Your Emotion</h3>
                <div className="flex flex-wrap gap-2">
                  {[
                    { emoji: "😊", label: "Happy" },
                    { emoji: "💙", label: "Helpful" },
                    { emoji: "🙏", label: "Hopeful" },
                    { emoji: "❤️", label: "Caring" },
                    { emoji: "😢", label: "Struggling" },
                    { emoji: "🌟", label: "Joyful" },
                    { emoji: "🌱", label: "Determined" },
                    { emoji: "🤗", label: "Grateful" },
                    { emoji: "💪", label: "Strong" },
                    { emoji: "😰", label: "Worried" },
                  ].map(em => (
                    <button
                      key={em.label}
                      onClick={() => setShowPost(true)}
                      className="flex items-center gap-1 bg-gray-50 hover:bg-rose-50 border border-gray-100 hover:border-rose-200 text-gray-600 hover:text-rose-500 text-xs px-2.5 py-1.5 rounded-full transition-colors cursor-pointer whitespace-nowrap"
                    >
                      {em.emoji} {em.label}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => setShowPost(true)}
                  className="w-full mt-3 bg-rose-50 hover:bg-rose-100 text-rose-500 font-semibold text-sm py-2.5 rounded-full transition-colors cursor-pointer whitespace-nowrap"
                >
                  Share How You Feel
                </button>
              </div>

              {/* Who can help */}
              <div className="bg-white rounded-2xl p-5">
                <h3 className="font-bold text-gray-800 mb-3 text-sm">Who's Here to Help</h3>
                <div className="flex flex-col gap-2">
                  {[
                    { icon: "ri-stethoscope-line", label: "Doctors & Pediatricians", count: "340+", color: "text-blue-500 bg-blue-50" },
                    { icon: "ri-leaf-line", label: "Nutritionists", count: "120+", color: "text-teal-500 bg-teal-50" },
                    { icon: "ri-hand-heart-line", label: "Volunteers", count: "2,800+", color: "text-green-500 bg-green-50" },
                    { icon: "ri-gift-line", label: "Donors", count: "7,100+", color: "text-amber-500 bg-amber-50" },
                    { icon: "ri-community-line", label: "Social Workers", count: "480+", color: "text-purple-500 bg-purple-50" },
                  ].map(r => (
                    <div key={r.label} className="flex items-center gap-3">
                      <div className={`w-8 h-8 flex items-center justify-center rounded-lg shrink-0 ${r.color}`}>
                        <i className={`${r.icon} text-sm`}></i>
                      </div>
                      <span className="text-sm text-gray-700 flex-1">{r.label}</span>
                      <span className="text-xs font-bold text-gray-500">{r.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
